import React, { useMemo, useState } from "react";

import { Search } from "lucide-react";

import { draftRuntime } from "./runtime";
import {
  IMPLEMENTATION_LABEL,
  RUNTIME_META,
  type SignalAdminDraft,
  type SignalAdminRegistry,
  draftTier,
  TIER_META,
} from "./types";

interface RegistryPanelProps {
  registry: SignalAdminRegistry;
  draft: SignalAdminDraft;
  onOpenSignal: (id: string) => void;
}

const selectClass =
  "h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-[12.5px] text-gray-800 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20";

const RegistryPanel: React.FC<RegistryPanelProps> = ({
  registry,
  draft,
  onOpenSignal,
}) => {
  const [query, setQuery] = useState("");
  const [family, setFamily] = useState("");
  const [layer, setLayer] = useState("");
  const [status, setStatus] = useState("");
  const [runtime, setRuntime] = useState("");

  const layerLabel = (id: string) =>
    registry.layers.find(l => l.id === id)?.label ?? id;

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return registry.definitions.filter(d => {
      if (
        q &&
        ![d.id, d.title, d.trigger, d.cta].join(" ").toLowerCase().includes(q)
      )
        return false;
      if (family && d.family !== family) return false;
      if (layer && !d.layers.includes(layer)) return false;
      if (status && d.implementation_status !== status) return false;
      if (runtime && draftRuntime(d, draft) !== runtime) return false;
      return true;
    });
  }, [registry.definitions, query, family, layer, status, runtime, draft]);

  const groups = Object.entries(registry.families)
    .map(([id, label]) => ({
      id,
      label,
      items: results.filter(d => d.family === id),
    }))
    .filter(g => g.items.length > 0);

  const filtersActive = Boolean(query || family || layer || status || runtime);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="text-[15px] font-bold text-gray-900">Signal Registry</h3>
        <p className="mt-0.5 max-w-3xl text-[12.5px] text-gray-500">
          The canonical catalogue plus proposals under review, grouped by
          family. Open a signal to see its dependencies and parameters and to
          turn its evaluation or user delivery on or off for all organizations.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[240px] flex-1 sm:flex-none">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            aria-hidden
          />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search name, key, trigger or CTA…"
            className="h-9 w-full rounded-lg border border-gray-300 pl-8 pr-3 text-[12.5px] text-gray-900 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 sm:w-[260px]"
          />
        </div>
        <select
          aria-label="Family"
          value={family}
          onChange={e => setFamily(e.target.value)}
          className={selectClass}
        >
          <option value="">All families</option>
          {Object.entries(registry.families).map(([id, label]) => (
            <option key={id} value={id}>
              {label}
            </option>
          ))}
        </select>
        <select
          aria-label="Data layer"
          value={layer}
          onChange={e => setLayer(e.target.value)}
          className={selectClass}
        >
          <option value="">All layers</option>
          {registry.layers.map(l => (
            <option key={l.id} value={l.id}>
              {l.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Implementation"
          value={status}
          onChange={e => setStatus(e.target.value)}
          className={selectClass}
        >
          <option value="">All statuses</option>
          {Object.entries(IMPLEMENTATION_LABEL).map(([id, label]) => (
            <option key={id} value={id}>
              {label}
            </option>
          ))}
        </select>
        <select
          aria-label="Runtime"
          value={runtime}
          onChange={e => setRuntime(e.target.value)}
          className={selectClass}
        >
          <option value="">All runtime states</option>
          {Object.entries(RUNTIME_META).map(([id, meta]) => (
            <option key={id} value={id}>
              {meta.label}
            </option>
          ))}
        </select>
        {filtersActive && (
          <button
            type="button"
            onClick={() => {
              setQuery("");
              setFamily("");
              setLayer("");
              setStatus("");
              setRuntime("");
            }}
            className="text-xs font-semibold text-brand hover:underline"
          >
            Clear filters
          </button>
        )}
        <span className="ml-auto text-xs text-gray-500">
          {results.length} of {registry.definitions.length} signals
        </span>
      </div>

      {groups.length === 0 && (
        <div className="rounded-xl border border-gray-200 bg-white px-5 py-10 text-center text-[12.5px] text-gray-500">
          No signals match these filters.
        </div>
      )}

      {groups.map(group => (
        <section
          key={group.id}
          className="overflow-hidden rounded-xl border border-gray-200 bg-white"
        >
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
            <h4 className="text-[13px] font-bold text-gray-900">
              {group.label}
            </h4>
            <span className="text-[11.5px] text-gray-400">
              {group.items.length} signal{group.items.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] table-fixed border-collapse text-left">
              {/* Fixed widths so columns line up across every family group. */}
              <colgroup>
                <col className="w-12" />
                <col />
                <col className="w-[110px]" />
                <col className="w-[27%]" />
                <col className="w-[140px]" />
                <col className="w-[130px]" />
                <col className="w-[64px]" />
              </colgroup>
              <thead>
                <tr className="bg-gray-50 text-[10px] font-bold uppercase tracking-[0.07em] text-gray-400">
                  <th className="w-12 px-4 py-2">#</th>
                  <th className="px-3 py-2">Signal</th>
                  <th className="px-3 py-2">Tier</th>
                  <th className="px-3 py-2">Needs</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Runtime</th>
                  <th className="px-4 py-2 text-right">Open</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {group.items.map(d => {
                  const rt = draftRuntime(d, draft);
                  const staged = rt !== d.state.runtime;
                  return (
                    <tr
                      key={d.id}
                      onClick={() => onOpenSignal(d.id)}
                      className="cursor-pointer align-top hover:bg-brand-faint"
                    >
                      <td className="px-4 py-2.5 font-mono text-[11px] text-gray-400">
                        {d.order}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="text-[12.5px] font-semibold text-gray-900">
                          {d.title}
                        </div>
                        <div className="mt-0.5 font-mono text-[10.5px] text-gray-400">
                          {d.id}
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10.5px] font-bold ${TIER_META[draftTier(d, draft)].className}`}
                        >
                          {TIER_META[draftTier(d, draft)].label}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex max-w-[260px] flex-wrap gap-1">
                          {d.layers.map(l => (
                            <span
                              key={l}
                              className={`rounded border px-1.5 py-0.5 text-[10.5px] ${
                                draft.layers[l] === false
                                  ? "border-red-200 bg-red-50 text-red-700 line-through"
                                  : "border-gray-200 bg-gray-50 text-gray-600"
                              }`}
                            >
                              {layerLabel(l)}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-[12px] text-gray-600">
                        {IMPLEMENTATION_LABEL[d.implementation_status]}
                        {d.review !== "Keep" &&
                          (draft.review_resolved[d.id] ? (
                            <div className="mt-0.5 text-[10.5px] font-semibold text-emerald-700">
                              Review resolved
                            </div>
                          ) : (
                            <div className="mt-0.5 text-[10.5px] font-semibold text-purple-700">
                              Review: {d.review}
                            </div>
                          ))}
                      </td>
                      <td className="px-3 py-2.5">
                        <span
                          className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[10.5px] font-bold ring-1 ring-inset ${RUNTIME_META[rt].className}`}
                        >
                          {RUNTIME_META[rt].label}
                        </span>
                        {staged && (
                          <div className="mt-1 text-[10.5px] font-semibold text-amber-700">
                            Unapplied
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right text-[12.5px] font-semibold tabular-nums text-gray-700">
                        {d.open_firings || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
};

export default RegistryPanel;
